import { Injectable, inject } from '@angular/core';
import { IUserPreferenceModel, UserPreferenceHelper } from '../../model/user/user-preference.model';
import { Observable, of, take, tap } from 'rxjs';
import { UserContextService } from '../../context/user.context.service';
import { WhoAmI } from '../../model/user/whoAmI.model';
import { Theme } from '../../model/user/theme.enum';
import { HttpClient, HttpParams } from '@angular/common/http';
import { IUserPreferenceRequest } from '../../model/user/user-preference-request.model';
import { UserPreferenceConstant } from '../../constant/base.constant';
import { isEqual } from 'lodash-es';
import { IUserKeyModel } from '../../model/user-key.model';
import { EnvironmentConfig } from '../../model/environment.model';
import { ENV_CONFIG } from '../../injection-tokens/environment.token';
import { ThemeService } from '../theme-service/theme.service';

@Injectable({
    providedIn: 'root',
})
export class UserPreferenceService {
    private _userContextService: UserContextService = inject(UserContextService);
    private _themeService: ThemeService = inject(ThemeService);
    private _httpClient: HttpClient = inject(HttpClient);
    private _environment: EnvironmentConfig = inject(ENV_CONFIG);
    private _userPreferencePath: string = 'userpreference';

    private _userInSession!: WhoAmI;
    private _userPreferences = new Map<string, Map<string, Map<string, string>>>();

    constructor() {
        this._userContextService
            .getContext()
            .pipe(take(1))
            .subscribe((userContext: WhoAmI | null) => {
                if (userContext) {
                    this._userInSession = userContext;
                }
            });
    }

    public saveUserTheme(theme: Theme): Observable<IUserPreferenceModel> {
        const userPreference: IUserPreferenceModel = {
            userKey: this._userInSession?.getUserKey(),
            category: UserPreferenceConstant.CATEGORY_UI,
            subCategory: UserPreferenceConstant.SUBCATEGORY_LAYOUT,
            attributeKey: UserPreferenceConstant.ATTRIBUTEKEY_THEME,
            attributeValue: theme,
        } as IUserPreferenceModel;

        return this._saveUserPreference(userPreference).pipe(take(1));
    }

    public getUserPreference(request: IUserPreferenceRequest): string | null {
        const categoryMap: Map<string, Map<string, string>> | undefined = this._userPreferences.get(request.category);

        if (categoryMap && request.subcategory) {
            const subcategoryMap: Map<string, string> | undefined = categoryMap.get(request.subcategory);

            if (subcategoryMap && request.attributeKey) {
                const attribute = subcategoryMap.get(request.attributeKey);

                if (attribute) {
                    return attribute;
                }
            }
        }

        return null;
    }

    public getUserPreferenceForCategory(request: IUserPreferenceRequest): Map<string, string> | null {
        const categoryMap: Map<string, Map<string, string>> | undefined = this._userPreferences.get(request.category);

        if (categoryMap && request.subcategory) {
            const subcategoryMap: Map<string, string> | undefined = categoryMap.get(request.subcategory);
            if (subcategoryMap) {
                return subcategoryMap;
            }
        }

        return null;
    }

    public setUserPreference(model: IUserPreferenceModel): Observable<IUserPreferenceModel> {
        const request = this.getUserPreference(UserPreferenceHelper.getRequestFromModel(model));
        if (isEqual(model.attributeValue, request)) {
            return of(model);
        }

        this._storePreference(model);
        return this._saveUserPreference(model);
    }

    public initializeUserPreferences(userKey: IUserKeyModel): Observable<IUserPreferenceModel[]> {
        return this._getUserPreferences(userKey).pipe(
            take(1),
            tap((preferences: IUserPreferenceModel[]) => {
                this._initializeUserTheme(preferences);
                this._initializeUserPreferences(preferences);
            })
        );
    }

    private _initializeUserTheme(preferences: IUserPreferenceModel[]) {
        let parsedTheme: Theme = Theme.light;
        const theme: Theme | string =
            (preferences.find((pref) => pref.attributeKey === UserPreferenceConstant.ATTRIBUTEKEY_THEME)
                ?.attributeValue as Theme) ?? Theme.light;

        // Clean up all the ways UI theme has been stored in localStorage in the past
        try {
            const themeValue = JSON.parse(theme) as { theme?: Theme; isDefaultTheme?: boolean };
            if (themeValue.theme) {
                parsedTheme = themeValue.theme as Theme;
            }

            if (themeValue.isDefaultTheme) {
                parsedTheme = themeValue.isDefaultTheme ? Theme.light : Theme.dark;
            }
        } catch {
            parsedTheme = theme as Theme;
        }

        this._themeService.setTheme(parsedTheme);
    }

    private _initializeUserPreferences(preferences: IUserPreferenceModel[]) {
        this._userPreferences.clear();
        const value = new Map<string, Map<string, Map<string, string>>>();
        preferences = preferences.filter(
            (preference: IUserPreferenceModel) => preference.attributeKey !== UserPreferenceConstant.ATTRIBUTEKEY_THEME
        );

        preferences.forEach((preference) => {
            if (!value.has(preference.category)) {
                value.set(preference.category, new Map());
            }

            if (!value.get(preference.category)?.has(preference.subCategory)) {
                value.get(preference.category)?.set(preference.subCategory, new Map());
            }

            value
                .get(preference.category)
                ?.get(preference.subCategory)
                ?.set(preference.attributeKey, preference.attributeValue);
        });

        this._userPreferences = value;
    }

    private _getUserPreferences(userKey: IUserKeyModel): Observable<IUserPreferenceModel[]> {
        const params = new HttpParams({ fromObject: { ...userKey } });
        return this._httpClient.get<IUserPreferenceModel[]>(
            `${this._environment.APP.VC_USER_PREFERENCE_BASE_URL}/${this._userPreferencePath}/list`,
            {
                params: params,
            }
        );
    }

    private _saveUserPreference(userPreference: IUserPreferenceModel): Observable<IUserPreferenceModel> {
        return this._httpClient.post<IUserPreferenceModel>(
            `${this._environment.APP.VC_USER_PREFERENCE_BASE_URL}/${this._userPreferencePath}/save`,
            userPreference
        );
    }

    private _storePreference(model: IUserPreferenceModel) {
        this._userPreferences
            .get(model.category)
            ?.get(model.subCategory)
            ?.set(model.attributeKey, model.attributeValue);
    }

    public clear() {
        this._themeService.setTheme(Theme.light);
        this._userPreferences.clear();
    }
}
